<template>
    <div>
        <rkm-list
            :items="data.model.pedidosExames"
            hide-filters
            hide-pagination
        >
            <template v-slot="{ item, index }">
                <rkm-row>
                    <rkm-col cols="auto">
                        <rkm-col v-if="Number.isInteger(item.id)">
                            <rkm-icon
                                :id="`pronto-atendimento-exames-info-${item.id}`"
                                icon="info-circle"
                            />

                            <rkm-popover
                                :target="`pronto-atendimento-exames-info-${item.id}`"
                                triggers="hover"
                            >
                                <div>{{ item.centroCusto.setor.nome }}</div>
                                <div>
                                    <rkm-icon icon="user" />
                                    {{ item.lotacao.profissional.nome }} - {{ item.lotacao.ocupacao.codigo }}
                                </div>
                                <div> {{ item.centroCusto.infraestrutura.nome }} </div>
                            </rkm-popover>
                        </rkm-col>
                    </rkm-col>

                    <rkm-col cols="3">
                        {{ item.dataSolicitacao }}
                    </rkm-col>

                    <rkm-col cols="auto">
                        <div v-if="isEncaminhamento()">
                            {{ item.medicoSolicitanteEncaminhamento }}
                        </div>
                        <div v-else>
                            {{ `${item.lotacao.profissional.nome} - ${item.lotacao.ocupacao.nome} (${item.lotacao.ocupacao.codigo}) ` }}
                        </div>
                        <div>
                            <template v-if="item.urgente">
                                <i
                                    v-rkm-tooltip.hover
                                    title="Urgente"
                                >
                                <rkm-icon
                                    color="red"
                                    icon="exclamation-circle"
                                    small
                                />
                                </i>
                            </template>
                            <strong> Justificativa: </strong>{{ item.justificativa }}
                        </div>
                    </rkm-col>

                    <rkm-col class="text-right col-min">
                        <rkm-button
                            @click.stop="handleExcluirPedido(index)"
                            :disabled="Number.isInteger(item.id)"
                            color="danger"
                            small
                        >
                            <rkm-icon icon="trash" />
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </template>

            <template v-slot:collapse="{ item, index }">
                <rkm-list-group class="rkm-list-group">
                    <rkm-area title="Exames" class="p-2">
                        <rkm-list-group-item
                            v-for="(exame, exameIndex) in item.exames"
                            v-bind:key="uniqueId(exame.exame.id)"
                        >
                            <rkm-row class="align-items-center">
                                <rkm-col cols="2">
                                    <strong>Exame:</strong><br>{{ exame.exame.nome }}
                                </rkm-col>

                                <rkm-col>
                                    <strong>Procedimento:</strong><br>{{ exame.exame.procedimento.codigo }} - {{ exame.exame.procedimento.nome }}
                                </rkm-col>

                                <rkm-col cols="auto">
                                    <div v-if="exame.exame.observacao">
                                        <rkm-button
                                            small
                                            title="Observações"
                                            @click="$refs.ModalObservacao.open({ item, index  }, { exame, exameIndex })"
                                        >
                                            <rkm-icon icon="comment"/>
                                        </rkm-button>
                                    </div>
                                </rkm-col>

                                <rkm-col cols="auto">
                                    <rkm-form-select
                                        v-model="exame.status.id"
                                        value-field="id"
                                        text-field="nome"
                                        :label="false"
                                        class="status p-0 m-0"
                                        @change="setStatus(exame, $event)"
                                        :options="data.form.statusExame"
                                        :disabled="exame.disabled"
                                    />
                                </rkm-col>

                                <rkm-col
                                    cols="auto"
                                    class="pl-0"
                                >
                                     <rkm-form-time-picker
                                        name="Horário"
                                        class="horario p-0 m-0"
                                        right
                                        v-model="exame.horarioRealizacao"
                                        @blur="validateTime(exame)"
                                        @hidden="validateTime(exame)"
                                         :disabled="exame.disabledHorario || exame.status.id !== enums.statusExame.REALIZADO"
                                        :validations="{ required: exame.status.id === enums.statusExame.REALIZADO }"
                                    />
                                </rkm-col>

                                <rkm-col
                                    v-if="showProfissionalExecutante"
                                    cols="auto"
                                    class="pl-0"
                                >
                                    <rkm-form-select
                                        :disabled="exame.disabled
                                            || exame.status.id !== enums.statusExame.REALIZADO
                                            || (data.form.lotacoesExecutantes || []).length === 1"
                                        :label="false"
                                        :options="data.form.lotacoesExecutantes || []"
                                        :validations="{ required: exame.status.id === enums.statusExame.REALIZADO }"
                                        class="profissional p-0 m-0"
                                        default-option-text="Selecione o profissional"
                                        name="Profissional"
                                        show-default-option
                                        text-field="descricao"
                                        v-model="exame.lotacaoExecutanteId"
                                        value-field="id"
                                        :title="exame.lotacaoExecutanteId ? data.form.lotacoesExecutantes
                                            .find(l => l.id === exame.lotacaoExecutanteId).descricao : ''"
                                        v-rkm-tooltip.hover
                                    />
                                </rkm-col>
                            </rkm-row>
                        </rkm-list-group-item>
                    </rkm-area>

                    <rkm-area title="Laudos" class="p-2">
                        <template slot="trailing">
                            <rkm-button
                                color="primary"
                                small
                                @click="$refs.LaudoModal.open(index)"
                                :disabled="disabledLaudo(item)"
                                v-model="data.form.laudoRealizado"
                                title="Adicionar"
                            >
                                <rkm-icon icon="plus" />
                            </rkm-button>

                        </template>

                         <rkm-list-group-item class="text-center" v-if="!item.files">
                            <rkm-row>
                                <rkm-col>
                                    Não há registros correspondentes à sua solicitação
                                </rkm-col>
                            </rkm-row>
                        </rkm-list-group-item>

                        <rkm-list-group-item v-else
                            v-for="(file, indexLaudo) in item.files"
                            v-bind:key="uniqueId(indexLaudo)"
                        >
                            <rkm-row>
                                <rkm-col cols="auto"></rkm-col>

                                 <rkm-col cols="9">
                                    <div class="mt-1">
                                        <strong>Arquivo</strong>: {{ file.name }}
                                    </div>
                                </rkm-col>

                                <rkm-col cols="2" class="text-right">
                                    <rkm-button
                                        @click.stop="Number.isInteger(file.id) ? downloadLaudo(file) : checkLaudo(file)"
                                        color="primary"
                                        small
                                        title="Visualizar"
                                    >
                                        <rkm-icon icon="download" />
                                    </rkm-button>

                                    <rkm-button
                                        @click.stop="handleExcluirLaudo(indexLaudo, index)"
                                        :disabled="Number.isInteger(file.id)"
                                        color="danger"
                                        small
                                        title="Excluir"
                                    >
                                        <rkm-icon icon="trash" />
                                    </rkm-button>
                                </rkm-col>
                            </rkm-row>
                        </rkm-list-group-item>
                    </rkm-area>
                </rkm-list-group>
            </template>
        </rkm-list>
        <modal-observacao ref="ModalObservacao" @updateObservacao="handleObservacao" />
        <laudo-pedido ref="LaudoModal" @updateLaudo="handleLaudo" :key="laudoComponentKey"/>
    </div>
</template>

<script>
//Enums
import tipoExame from '@/enums/parametrizacao/tipoExame'
import statusExame from '@/enums/pronto-atendimento/statusExame'

// Modals
import ModalObservacao from './components/observacao/Modal'
import LaudoPedido from './components/laudo/Modal'

//Mixins
import { error, repository } from '@/mixins'

import moment from 'moment'

import { mapActions } from 'vuex'

export default {
    components: { ModalObservacao, LaudoPedido },
    data: () => ({
        laudoComponentKey: 0,
        enums: {
            tipoExame,
            statusExame
        },
        repositories: {
            laudo: null,
            dataHora: null,
        },
        items: []
    }),
    props: {
        data: { type: Object, required: true },
        showProfissionalExecutante: { type: Boolean, default: false },
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        disabledLaudo(item) {

            let hasRealizado = false

            if (item.exames.find(e => e.status.id == this.enums.statusExame.REALIZADO)) {

                hasRealizado = true
            }

            return !hasRealizado
        },
        isEncaminhamento() {

            return this.data.form.recepcao.isEncaminhamento
        },
        preparaExamesItems() {

            this.data.form.pedidosExames = this.data.form.pedidosExames.map(pedido => {
                return { ...pedido, isForm: true}
            })

            this.data.model.pedidosExames = this.data.model.pedidosExames.concat(this.data.form.pedidosExames)
        },
        setStatus(exame, statusId) {
            exame.statusId = statusId
            exame.status.id = statusId // Provisório

            if (exame.status.id !== this.enums.statusExame.REALIZADO) {

                exame.horarioRealizacao = null
                exame.lotacaoExecutanteId = null
            }
            else {

                if (this.data.form.lotacoesExecutantes && this.data.form.lotacoesExecutantes.length === 1) {

                    exame.lotacaoExecutanteId = this.data.form.lotacoesExecutantes[0].id
                }
            }
        },
        validateTime(exame) {

            this.$nextTick().then(() => {

                if (exame.horarioRealizacao) {

                    const horarioRealizacao = moment(exame.horarioRealizacao, 'HH:mm')

                    this.setLoading()

                    this.repositories.dataHora.get({ formato: 'd/m/Y H:i' })
                        .then(({ data }) => {

                            const dataHoraAtual = moment(data, 'DD/MM/YYYY HH:mm')
                            const dataHoraRealizacao = dataHoraAtual.clone()

                            dataHoraRealizacao.hour(horarioRealizacao.hour())
                            dataHoraRealizacao.minute(horarioRealizacao.minute())

                            if (dataHoraRealizacao > dataHoraAtual) {

                                this.showValidationNotification(
                                    'A hora inserida no exame não pode ser maior que a hora atual.'
                                )

                                exame.horarioRealizacao = null
                            }
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                }
            })
        },
        handleExcluirPedido(index) {

           this.data.model.pedidosExames.splice(index, 1)
        },
        handleObservacao(item) {
            this.data.model.pedidosExames[item.pedidoExame.index]
                .exames[item.pedidoExame.exame.index].observacao = item.pedidoExame.exame.observacao
        },
        handleLaudo(pedido) {

            if(pedido.files.length) {

                this.data.model.pedidosExames[pedido.index].files.push(...pedido.files)
                this.laudoComponentKey++
            }
        },
        handleExcluirLaudo(indexLaudo, indexPedido) {

            const destroy = async () => this.data.model.pedidosExames[indexPedido].files.splice(indexLaudo, 1)

            this.showDeleteConfirmation('Deseja realmente excluir o laudo?', destroy)
        },
        downloadLaudo(laudo) {

            this.repositories.laudos.download(laudo.id, { hash: laudo.hash }).then(response => {
                const file = new Blob([response.data], { type: "application/pdf" }),
                    url = window.URL.createObjectURL(file)

                window.open(url)
            })
        },
        checkLaudo(laudo) {
            window.open(URL.createObjectURL(laudo), '_blank')
        },
    },
    mixins: [ repository, error ],
    created() {
        this.repositories.laudos = this.getConectaSusRepository('prontoAtendimento.laudoExame')
        this.repositories.dataHora = this.getConectaSusRepository('dataHora')
        this.preparaExamesItems()
    }
}
</script>

<style scoped>
.status { width: 240px; }
.horario { width: 125px; }
.profissional { width: 228px; }
</style>